import { supabase } from "@/integrations/supabase/client";
import {
  NATIVE_OAUTH_REDIRECT_HOST,
  NATIVE_OAUTH_REDIRECT_SCHEME,
} from "@/lib/auth/oauthRedirect";

const OAUTH_RETURN_PREFIX = `${NATIVE_OAUTH_REDIRECT_SCHEME}://${NATIVE_OAUTH_REDIRECT_HOST}`;

let oauthReturnInFlight = false;

/**
 * Verarbeitet OAuth-Rückkehr per Custom-Scheme (Android/iOS WebView).
 * PKCE: code_verifier liegt in derselben WebView wie signInWithOAuth → exchangeCodeForSession hier.
 */
export async function handleOAuthReturnUrl(url: string): Promise<boolean> {
  console.log("[OAuthReturn] handleOAuthReturnUrl =", url);

  if (!url.includes(OAUTH_RETURN_PREFIX)) {
    console.log(
      "[OAuthReturn] skip: URI enthält nicht",
      OAUTH_RETURN_PREFIX
    );
    return false;
  }
  if (oauthReturnInFlight) {
    console.log("[OAuthReturn] skip: bereits in Bearbeitung (inFlight)");
    return false;
  }
  oauthReturnInFlight = true;
  try {
    try {
      const parsed = new URL(url);
      console.log("[OAuthReturn] search =", parsed.search);
    } catch (parseErr) {
      console.log("[OAuthReturn] URL() parse Fehler (ok bei manuellem Query-Parse):", parseErr);
    }

    const qIdx = url.indexOf("?");
    const qs = qIdx >= 0 ? url.slice(qIdx + 1) : "";
    const params = new URLSearchParams(qs);
    const code = params.get("code");
    const oauthError = params.get("error");
    const oauthErrorDesc = params.get("error_description");

    console.log("[OAuthReturn] code =", code);
    console.log("[OAuthReturn] error =", oauthError);
    console.log("[OAuthReturn] error_description =", oauthErrorDesc);

    if (oauthError) {
      const msg = oauthErrorDesc || oauthError || "OAuth-Fehler";
      window.location.assign(`/login?oauth_error=${encodeURIComponent(msg)}`);
      return true;
    }

    if (!code) {
      console.log("[OAuthReturn] kein code → Abbruch (kein exchange)");
      return false;
    }

    const { data: exchangeData, error: exchangeError } =
      await supabase.auth.exchangeCodeForSession(code);
    console.log(
      "[OAuthReturn] exchange result data.session =",
      Boolean(exchangeData?.session)
    );
    console.log("[OAuthReturn] exchange result error =", exchangeError);

    if (exchangeError) {
      console.error("[OAuth] exchangeCodeForSession", exchangeError);
      window.location.assign(
        `/login?oauth_error=${encodeURIComponent(exchangeError.message)}`
      );
      return true;
    }

    params.delete("code");
    params.delete("state");
    const rest = params.toString();
    window.location.assign(`/auth/callback${rest ? `?${rest}` : ""}`);
    return true;
  } finally {
    oauthReturnInFlight = false;
  }
}

/**
 * Nur native Capacitor: Listener für Deep-Link nach Google-Login (externer Browser / CCT).
 */
export function registerCapacitorOAuthDeepLink(): void {
  if (typeof window === "undefined") {
    return;
  }

  void (async () => {
    const { Capacitor } = await import("@capacitor/core");
    if (Capacitor.getPlatform() === "web") {
      return;
    }

    const { App } = await import("@capacitor/app");

    const PAYMENT_SUCCESS_PREFIX = `${NATIVE_OAUTH_REDIRECT_SCHEME}://payment-success`;

    function handlePaymentReturnUrl(url: string): void {
      console.log("[PaymentReturn] handlePaymentReturnUrl =", url);
      const qIdx = url.indexOf("?");
      const qs = qIdx >= 0 ? url.slice(qIdx + 1) : "";
      window.location.assign(`/payment-success${qs ? `?${qs}` : ""}`);
    }

    const run = (url: string, source: "appUrlOpen" | "getLaunchUrl") => {
      console.log(`[DeepLink] ${source} =`, url);
      if (url.includes(OAUTH_RETURN_PREFIX)) {
        void handleOAuthReturnUrl(url);
      } else if (url.includes(PAYMENT_SUCCESS_PREFIX)) {
        handlePaymentReturnUrl(url);
      } else {
        console.log("[DeepLink] unbekanntes Schema, ignoriert:", url);
      }
    };

    await App.addListener("appUrlOpen", ({ url }) => {
      run(url, "appUrlOpen");
    });

    try {
      const launch = await App.getLaunchUrl();
      console.log("[OAuthReturn] getLaunchUrl raw =", launch);
      if (launch?.url) {
        run(launch.url, "getLaunchUrl");
      } else {
        console.log("[OAuthReturn] getLaunchUrl: keine url (undefined)");
      }
    } catch (e) {
      console.log("[OAuthReturn] getLaunchUrl catch =", e);
    }
  })();
}
